import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@/lib/utils"

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-sans text-sm font-medium tracking-[0.01em] ring-offset-background transition-all duration-150 ease-out focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50",
  {
    variants: {
      variant: {
        // Ink fill, paper text, soft ink shadow — the single primary action per region.
        default:
          "rounded-sm bg-ink text-paper shadow-auvers-btn hover:bg-[#0d1a31] hover:-translate-y-px",
        // Ghost — transparent fill, ink text, hovers gold. Used for secondary actions.
        ghost:
          "rounded-sm bg-transparent text-ink hover:text-gold hover:bg-transparent",
        // Outline — paper-tinted with rule border.
        outline:
          "rounded-sm border border-rule bg-transparent text-ink hover:bg-paper-2 hover:border-ink-soft/40",
        // Secondary — paper-2 fill for low-emphasis actions.
        secondary:
          "rounded-sm bg-paper-2 text-ink hover:bg-paper-3",
        // Destructive — tonal ink with no neon red, per Auvers anti-third-accent rule.
        destructive:
          "rounded-sm bg-[#8a3a2a] text-paper hover:bg-[#6e2c20]",
        // Link — gold underline on hover.
        link:
          "text-ink underline-offset-4 hover:text-gold hover:underline",
      },
      size: {
        default: "h-10 px-[22px] py-[14px]",
        sm: "h-9 px-4 py-2 text-[13px]",
        lg: "h-12 px-7 py-4",
        icon: "h-10 w-10 rounded-sm",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button, buttonVariants }
